import { dayIsBefore, parseUtcDay } from '@lib/helpers/calendar-day'
import type { EventLifecycle } from '@lib/enums/event-lifecycle.enum'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import type { EventStatusKey } from '@/components/EventsFilters'

/**
 * An event's status, derived from its date — the data has no explicit status
 * field: undated events are drafts, past events are done, the rest are
 * scheduled.
 *
 * **The same day comparison the API makes**, and it has to be: `date` is a
 * calendar day, so `new Date(date).getTime() < Date.now()` called an event past
 * from its own midnight. The API's `statusWhere` stopped doing that, which left
 * the browser drawing a "Past" tag on a row the API had just returned as
 * scheduled — the two answering differently about one event, in the same table.
 *
 * Day-grained and UTC-anchored, matching the server. The venue's own clock is
 * what this eventually wants; see `docs/event-scheduling.md`.
 */
export function eventStatusKey(
  event: { date: CalendarDay | null; lifecycle?: EventLifecycle | undefined },
  now: Date = new Date(),
): EventStatusKey {
  // **Canceled first, because it is a decision and outranks the clock.** A
  // canceled booking keeps its date, so deriving from the date alone rendered
  // it as "Booked" — the one label it must never carry. The API has sent
  // `lifecycle` since the cancellation work; nothing here read it.
  if (event.lifecycle === 'lost') return 'lost'
  if (event.lifecycle === 'cancelled') return 'cancelled'
  if (event.date === null) return 'enquiry'
  if (dayIsBefore(parseUtcDay(event.date), now)) return 'past'
  return 'booked'
}

/**
 * The filtering and ordering that used to live here is in the API now
 * (`api/src/events/event-list-query.ts`, with the same cases in its spec).
 *
 * It had to move: a list is paged before it reaches the browser, and a filter
 * applied to a page shows three rows of fifty and calls it the answer. What is
 * left is the derivation above, which the table still needs in order to draw a
 * row's status tag — the same rule the API filters by, from the same fact.
 */
